import React from 'react'
import * as yup from 'yup'
import { Formik } from 'formik'
import { useSelfSignupFormFieldsContext } from './SelfSignupFormFieldsContext'
import { AllowedUserFieldsEnum } from 'shared/accountNameConfig'
import { getValidationSchemaForUserFields } from 'shared/entities-validator/user-fields-validators'
import { t } from '@lingui/macro'

type WithSelfSignupFormikProps = {
  onSubmit: (formData: Record<AllowedUserFieldsEnum, string>) => void
}

const useFormikConfig = (): {
  validationSchemaWithPassword: yup.AnySchema
  initialValues: Record<AllowedUserFieldsEnum, string>
} => {
  const { fields: requiredFields, privacyPolicyUrl } =
    useSelfSignupFormFieldsContext()

  return React.useMemo(() => {
    return {
      validationSchemaWithPassword: yup.object().shape(
        getValidationSchemaForUserFields({
          errorMessages: {
            passwordValidationMessage: t`Passwords must be at least 8 characters containing both letters and numbers.`,
            defaultNonNullMessage: t`must not be empty`,
            privacyPolicyValidationMessage: t`You must accept the Privacy Policy in order to continue`,
            bothPasswordNeedToBeTheSameMessage: t`Both password need to be the same`,
            emailRequiredMessage: 'email is required'
          },
          requireAdditionalFields: true,
          requiredFields
        })
      ),
      initialValues: Object.fromEntries(
        requiredFields.map((field) => [field, ''])
      ) as Record<AllowedUserFieldsEnum, string>
    }
  }, [requiredFields, privacyPolicyUrl])
}

export const WithSelfSignupFormik: React.FC<WithSelfSignupFormikProps> = ({
  children,
  onSubmit
}) => {
  const { validationSchemaWithPassword, initialValues } = useFormikConfig()

  return (
    <Formik
      onSubmit={onSubmit}
      initialValues={initialValues}
      enableReinitialize
      validateOnMount
      validationSchema={validationSchemaWithPassword}
    >
      {children}
    </Formik>
  )
}
